import { useState, useEffect, useCallback } from 'react';
import { rasService } from '../services/rasService';
import { Configuration } from '../types/api';

interface UseConfigurationResult {
  config: Configuration | null;
  loading: boolean;
  error: string | null;
  updating: boolean;
  updateConfig: (settings: Partial<Configuration>) => Promise<void>;
  resetConfig: () => Promise<void>;
  refetch: () => void;
}

export const useConfiguration = (): UseConfigurationResult => {
  const [config, setConfig] = useState<Configuration | null>(null);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const configData = await rasService.getConfiguration();
      setConfig(configData);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch configuration');
      setConfig(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const updateConfig = useCallback(async (settings: Partial<Configuration>) => {
    try {
      setUpdating(true);
      setError(null);
      await rasService.updateConfiguration(settings);
      await fetchConfig();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update configuration');
      throw err;
    } finally {
      setUpdating(false);
    }
  }, [fetchConfig]);

  const resetConfig = useCallback(async () => {
    try {
      setUpdating(true);
      setError(null);
      await rasService.resetConfiguration();
      await fetchConfig();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reset configuration');
      throw err;
    } finally {
      setUpdating(false);
    }
  }, [fetchConfig]);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  return { 
    config, 
    loading, 
    error, 
    updating, 
    updateConfig, 
    resetConfig, 
    refetch: fetchConfig 
  };
};